import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, CircularProgress, Paper } from '@mui/material';
import { Send } from '@mui/icons-material';
import { AnonMessage } from '../types';
import { formatRelative } from '../utils/formatters';

interface AnonThreadProps {
  messages: AnonMessage[];
  otherLabel: string;        // "your Secret Santa" or the recipient's name
  placeholder: string;
  emptyText: string;
  sending?: boolean;
  onSend: (body: string) => Promise<void> | void;
}

const AnonThread: React.FC<AnonThreadProps> = ({ messages, otherLabel, placeholder, emptyText, sending, onSend }) => {
  const [body, setBody] = useState('');

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    await onSend(text);
    setBody('');
  };

  return (
    <Box>
      {messages.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>{emptyText}</Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
          {messages.map((m) => (
            <Box key={m.id} sx={{ display: 'flex', justifyContent: m.fromMe ? 'flex-end' : 'flex-start' }}>
              <Paper
                elevation={0}
                sx={{
                  px: 1.5, py: 1, maxWidth: '85%',
                  bgcolor: m.fromMe ? 'primary.main' : 'grey.100',
                  color: m.fromMe ? 'white' : 'text.primary',
                  borderRadius: 3,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', textAlign: 'right' }}>
                  {m.fromMe ? 'You' : otherLabel} · {formatRelative(m.createdAt)}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth size="small" multiline maxRows={4} value={body} placeholder={placeholder}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          inputProps={{ maxLength: 1000 }}
        />
        <Button variant="contained" onClick={submit} disabled={sending || !body.trim()} sx={{ minWidth: 48, px: 1.5 }} aria-label="Send">
          {sending ? <CircularProgress size={20} /> : <Send fontSize="small" />}
        </Button>
      </Stack>
    </Box>
  );
};

export default AnonThread;
